import React, {useEffect, useState} from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFViewer,
  Font,
  Image
} from "@react-pdf/renderer";
import AppHelper from "../../helpers/AppHelper";

// ─── STYLES ──────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    paddingHorizontal: 30,
    paddingVertical: 28,
    fontSize: 8,
    fontFamily: "Helvetica",
    color: "#111",
    backgroundColor: "#fff",
  },

  // ── Header ──────────────────────────────────────────────────────────────
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 14,
  },
  logoBlock: {
    flexDirection: "row",
    alignItems: "center",
    height: 70
  },
  logoImage: {
    width: 70,
    height: 70,
    objectFit: "contain",
  },
  companyBlock: {
    marginLeft: 10,
    justifyContent: "center",
  },
  companyInstitution: {
    fontSize: 6.5,
    color: "#1a4b8c",
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
  },
  companyName: {
    fontSize: 16,
    color: "#1a4b8c",
    fontFamily: "Helvetica-Bold",
    letterSpacing: 1,
    lineHeight: 1.1,
  },
  companySlogan: {
    fontSize: 5.5,
    color: "#666",
    fontStyle: "italic",
  },
  companyAddress: {
    marginTop: 6,
  },
  companyAddressText: {
    fontSize: 7,
  },
  companyNameBold: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
  },

  // ── Boleta box ──────────────────────────────────────────────────────────
  boletaBox: {
    border: "1.5pt solid #222",
    borderRadius: 4,
    padding: "8 12",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 160,
    maxWidth: 180,
  },
  boletaRuc: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    marginBottom: 2,
  },
  boletaTipo: {
    fontSize: 9.5,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    lineHeight: 1.3,
  },
  boletaSerie: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    marginTop: 3,
  },

  // ── Info boxes row ───────────────────────────────────────────────────────
  infoRow: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 10,
  },
  infoBox: {
    border: "1pt solid #bbb",
    borderRadius: 3,
    padding: "7 10",
  },
  infoBoxLeft: {
    flex: 1.4,
  },
  infoBoxRight: {
    flex: 1,
  },
  infoFieldRow: {
    flexDirection: "row",
    marginBottom: 3,
  },
  infoLabel: {
    fontFamily: "Helvetica-Bold",
    width: 75,
    fontSize: 7.5,
  },
  infoValue: {
    flex: 1,
    fontSize: 7.5,
  },

  // ── Items table ──────────────────────────────────────────────────────────
  table: {
    border: "1pt solid #bbb",
    borderRadius: 3,
    marginBottom: 8,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f3f3f3",
    borderBottom: "1.5pt solid #bbb",
    paddingVertical: 5,
    paddingHorizontal: 6,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderBottom: "0.5pt solid #e8e8e8",
    minHeight: 18,
  },
  thCant: { width: 28, textAlign: "center", fontFamily: "Helvetica-Bold", fontSize: 7.5 },
  thUm:   { width: 24, textAlign: "left",   fontFamily: "Helvetica-Bold", fontSize: 7.5 },
  thCod:  { width: 28, textAlign: "left",   fontFamily: "Helvetica-Bold", fontSize: 7.5 },
  thDesc: { flex: 1,   textAlign: "left",   fontFamily: "Helvetica-Bold", fontSize: 7.5 },
  thVu:   { width: 52, textAlign: "right",  fontFamily: "Helvetica-Bold", fontSize: 7.5 },
  thPu:   { width: 52, textAlign: "right",  fontFamily: "Helvetica-Bold", fontSize: 7.5 },
  thImp:  { width: 52, textAlign: "right",  fontFamily: "Helvetica-Bold", fontSize: 7.5 },

  tdCant: { width: 28, textAlign: "center", fontSize: 7.5 },
  tdUm:   { width: 24, textAlign: "left",   fontSize: 7.5 },
  tdCod:  { width: 28, textAlign: "left",   fontSize: 7.5 },
  tdDesc: { flex: 1,   textAlign: "left",   fontSize: 7.5 },
  tdVu:   { width: 52, textAlign: "right",  fontSize: 7.5 },
  tdPu:   { width: 52, textAlign: "right",  fontSize: 7.5 },
  tdImp:  { width: 52, textAlign: "right",  fontSize: 7.5 },

  // ── Totals ───────────────────────────────────────────────────────────────
  totalsRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 1,
  },
  totalsLabel: {
    width: 110,
    textAlign: "right",
    paddingRight: 6,
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
  },
  totalsCurrency: {
    width: 16,
    textAlign: "right",
    fontSize: 7.5,
    color: "#444",
  },
  totalsValue: {
    width: 52,
    textAlign: "right",
    fontSize: 7.5,
    paddingRight: 6,
  },
  totalsValueBold: {
    width: 52,
    textAlign: "right",
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
    paddingRight: 6,
  },

  // ── Importe en letras ────────────────────────────────────────────────────
  letrasBox: {
    border: "1pt solid #bbb",
    borderRadius: 3,
    padding: "6 10",
    marginTop: 8,
    marginBottom: 6,
  },
  letrasText: {
    fontSize: 7.5,
  },
  letrasBold: {
    fontFamily: "Helvetica-Bold",
  },

  // ── Forma de pago ────────────────────────────────────────────────────────
  formaPagoText: {
    fontSize: 7.5,
    marginBottom: 8,
  },

  // ── Footer ───────────────────────────────────────────────────────────────
  footerBox: {
    border: "1pt solid #bbb",
    borderRadius: 3,
    padding: "8 10",
    flexDirection: "row",
    // alignItems: "flex-end",
    // alignItems: 'center',
    gap: 2,
  },
  footerText: {
    fontSize: 6.5,
    flex: 1,
    lineHeight: 1.5,
  },
  footerBold: {
    fontFamily: "Helvetica-Bold",
  },
  qrBox: {
    width: 60,
    height: 60,
    // border: "1.5pt solid #333",
    justifyContent: "center",
    alignItems: "center",
  },
  qrPlaceholder: {
    fontSize: 6,
    color: "#555",
    textAlign: "center",
  },

  divider: {
    borderBottom: "0.75pt solid #ccc",
    marginVertical: 6,
  },
});

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────

const InfoField = ({ label, value }) => (
  <View style={s.infoFieldRow}>
    <Text style={s.infoLabel}>{label}</Text>
    <Text style={s.infoValue}>{value !== undefined ? `${value}` : ""}</Text>
  </View>
);

const TotalRow = ({ label, value, bold }) => (
  <View style={s.totalsRow}>
    <Text style={s.totalsLabel}>{label}</Text>
    <Text style={s.totalsCurrency}>S/</Text>
    <Text style={bold ? s.totalsValueBold : s.totalsValue}>{value}</Text>
  </View>
);


// ─── DOCUMENT ─────────────────────────────────────────────────────────────────

const InvoicePDF = ({data}) => {
  // const { empresa, cliente, items, totales, importeLetras, formaPago } = boletaData;
  const {campus} = data;

  const [imageUrl, setImageUrl] = useState(null);

  const info = JSON.parse(campus.infoExtra);

  useEffect(() => {
    async function toDataURL() {
      try {
        const response = await fetch(data.logo,{
          method: 'GET'
        });
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          console.log(reader.result);
          setImageUrl(reader.result);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error("Error cargando la imagen por CORS:", error);
      }
    }
    if (data.logo) toDataURL();
  }, [data.logo]);

  return (
    <Document title={`${data.documentType === "01"
                ? "Factura"
                : "Boleta"} ${data.serie} - ${data.correlative}`} author={data.campus.company}>
      <Page size="A4" style={s.page}>

        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <View style={s.headerRow}>
          {/* Logo + empresa */}
          <View>
            <View style={s.logoBlock}>
              {
              <Image
                src={data.logo}
                style={{ width: 'auto', height:  '100%'}}
              />
              // <Image 
              //   src={{ 
              //     uri: campus.logo, 
              //     method: "GET", 
              //     headers: { "Cache-Control": "no-cache" }, 
              //     body: "" 
              //   }}
              //   style={{ width: 70, height: 70 }}
              //  /> 
              }
              {/* <Image style={{ width: 70, height: 70 }} src={{ uri: boletaData.empresa.logo, method: "GET", headers: { "Cache-Control": "no-cache" }, body: "" }} /> */}
            </View>
            <View style={s.companyAddress}>
                <Text style={s.companyNameBold}>{data.campus.company}</Text>
                <Text style={s.companyAddressText}>{data.campus.address}</Text>
                {/* <Text style={s.companyAddressText}>{empresa.ciudad}</Text> */}
              </View>
          </View>

          {/* Boleta box */}
          <View style={s.boletaBox}>
            <Text style={s.boletaRuc}>RUC {data.campus.ruc}</Text>
            <Text style={s.boletaTipo}>
            {data.documentType === "01"
                ? "FACTURA ELECTRÓNICA"
                : "BOLETA ELECTRÓNICA"}
            </Text>
            <Text style={s.boletaSerie}>N°: {data.serie} - {data.correlative}</Text>
          </View>
        </View>

        {/* ── INFO BOXES ─────────────────────────────────────────────────── */}
        <View style={s.infoRow}>
          {/* Cliente */}
          <View style={[s.infoBox, s.infoBoxLeft]}>
            <InfoField label="CLIENTE" value="" />
            <InfoField label="DNI / RUC" value={`: ${data.userDni}`} />
            <InfoField label="DENOMINACIÓN" value={`:  ${data.userName}`} />
            <InfoField label="DIRECCIÓN" value={`: ${data.userAddress}`} />
          </View>

          {/* Fechas */}
          <View style={[s.infoBox, s.infoBoxRight]}>
            <InfoField label="FECHA EMISIÓN" value={`: ${AppHelper.converterDate(data.paidAt)}`} />
            <InfoField label="FECHA DE VENC." value='' />
            <InfoField label="MONEDA" value={`: SOLES`} />
          </View>
        </View>

        {/* ── ITEMS TABLE ────────────────────────────────────────────────── */}
        <View style={s.table}>
          {/* Header */}
          <View style={s.tableHeader}>
            <Text style={s.thCant}>CANT.</Text>
            {/* <Text style={s.thUm}>UM</Text>
            <Text style={s.thCod}>CÓD.</Text> */}
            <Text style={s.thDesc}>DESCRIPCIÓN</Text>
            <Text style={s.thVu}>V/U</Text>
            <Text style={s.thPu}>P/U</Text>
            <Text style={s.thImp}>IMPORTE</Text>
          </View>

          {/* Data rows */}
          {data.paymentDetails.map((item, i) => (
            <View key={i} style={s.tableRow}>
              <Text style={s.tdCant}>{item.quantity}</Text>
              {/* <Text style={s.tdUm}>{item.um}</Text>
              <Text style={s.tdCod}>{item.cod}</Text> */}
              <Text style={s.tdDesc}>{item.description}</Text>
              <Text style={s.tdVu}>{item.amount}</Text>
              <Text style={s.tdPu}>{item.amount}</Text>
              <Text style={s.tdImp}>{item.amount}</Text>
            </View>
          ))}

          {/* Empty rows */}
          {Array.from({ length: 7 }).map((_, i) => (
            <View key={`e${i}`} style={s.tableRow} />
          ))}
        </View>

        {/* ── TOTALS ─────────────────────────────────────────────────────── */}
        <TotalRow label="EXONERADA"         value={data.amount} />
        {/* <TotalRow label="GRAVADA"           value={0} />
        <TotalRow label="IGV 18.00 %"       value={0} /> */}
        <TotalRow label="TOTAL"             value={data.amount} bold />
        <TotalRow label="TOTAL PAGADO"      value={data.amount} bold />
        {/* <TotalRow label="DIFERENCIA (VUELTO)" value={0} /> */}

        {/* ── IMPORTE EN LETRAS ──────────────────────────────────────────── */}
        <View style={s.letrasBox}>
          <Text style={s.letrasText}>
            <Text style={s.letrasBold}>IMPORTE EN LETRAS: </Text>
            {data.amountLetters}
          </Text>
        </View>

        {/* ── FORMA DE PAGO ──────────────────────────────────────────────── */}
        <Text style={s.formaPagoText}>
          <Text style={s.letrasBold}>FORMA DE PAGO</Text>: {`TRANSFERENCIA - DEPÓSITO [CONTADO]: S/ ${data.amount}`}
        </Text>

        {/* ── FOOTER ─────────────────────────────────────────────────────── */}
        <View style={s.footerBox}>
          <View style={{ flex: 1 }}>
            <Text style={s.footerText}>
              Representación impresa de la ELECTRÓNICA, para ver el documento
              visita{"\n"}
             {info.empresa === 'Facturactiva' ?  <Text style={s.footerBold}>{info.url}</Text>
             :
             <Text style={s.footerBold}>{`${info.url}`}{campus.ruc}</Text>
             }
              {"\n"}
              Emitido mediante un{" "}
              <Text style={s.footerBold}>PROVEEDOR Autorizado por la SUNAT</Text> mediante
              Resolución de Intendencia No. 
              <Text style={s.footerBold}>{info.resolucion}</Text>
            </Text>
          </View>

          {/* QR placeholder */}
          <View style={s.qrBox}>
            {/* <Text style={s.qrPlaceholder}>{"[QR\nCODE]"}</Text> */}
          </View>
        </View>

      </Page>
    </Document>
  );
};

export default InvoicePDF;